import * as React from 'react';
import { Res, TicketLineDTO, SoldProductType, Permission } from 'core/api';
import { Command } from 'pos/common';
import { Result } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import * as SalesStore from '../SalesStore';
import { CustomerDisplay } from 'pos/services';
import { Message, Modal } from 'core/ui-next/modal';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { z } from 'zod';
import { zMonetary } from 'core/infrastructure-next';
import { AcceptActionButton, BackActionButton, Content, Filler, Footer, Header, LargeDeviceOnly, PhoneOnly, PosScreen } from 'pos/ui';
import { BasicButton, SvgIcon } from 'core/ui-next';
import { VStack, Text, Span, Box } from '@chakra-ui/react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';

const formatMoney = UI.Format.asMoney;

type Props = {
	line: TicketLineDTO;
} & Modal.Actions<number>;

const stateSchema = z.object({
	initialAmount: zMonetary({ nonNegative: true })
});

type State = z.infer<typeof stateSchema>;

function GetPrice(props: Props) {
	const { line, cancel, close } = props;
	const formRef = React.useRef<FormRef<State>>(null);
	const { Form, DecimalInput } = useControlsFor<State>();

	const onAccept = async () => {
		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;

		const isValidPrice = result.value.initialAmount <= 9999999.99;
		if (!isValidPrice) {
			await Message.show(Res.Msg.MSG_INVALID_PRICE_VALUE());
			return;
		}

		close(result.value.initialAmount);
	};

	const onCancel = React.useCallback(async () => cancel(), [cancel]);


	return <PosScreen screenId='SET_TICKET_LINE_PRICE' size='small' useKeyPad={true}>

		<Header title={Res.Web.EDIT_TICKET_LINE_MODIFY_PRICE_LBL()} onClose={cancel} />
		<Content>
			<VStack paddingX={'6'} paddingY={'9'} h='full' w='full' gap={'6'}>
				<VStack gap={'4'} justifyItems={'center'} w='full'>
					<SvgIcon src={Images.Euro} w={'12'} h={'12'} />
					<VStack gap={'2'} justifyItems={'center'} w='full' textAlign={'center'}>
						<Text fontSize={'md'} fontWeight={'bold'}>
							{Res.Web.EDIT_TICKET_LINE_NEW_PRICE_LBL()}
						</Text>
						<Span fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>{Res.Web.EDIT_TICKET_LINE_ACTUAL_PRICE_LBL()} {formatMoney(line.UnitPrice)}</Span>
					</VStack>
				</VStack>
				<VStack w={'full'} justifyContent={'center'}>
					<Box w={'full'}>
						<Form ref={formRef} initialValue={{ initialAmount: line.UnitPrice }} schema={stateSchema}>
							<DecimalInput linkTo='initialAmount' onKeyDown={UI.whenEnterKey(onAccept)} />
						</Form>
					</Box>
				</VStack>
				<LargeDeviceOnly>
					<BasicButton type='success' text={Res.Common.OK_BTN()} onClick={onAccept} />
				</LargeDeviceOnly>
			</VStack>
		</Content>
		<Footer>
			<PhoneOnly>
				<BackActionButton action={onCancel} />
				<Filler />
				<AcceptActionButton action={onAccept} />
			</PhoneOnly>
		</Footer>

	</PosScreen>;
}

declare global {
	interface CommandType {
		setTicketLinePrice: (line: TicketLineDTO, price?: number) => Promise<Result>
	}
}

Command.register('setTicketLinePrice', Preconditions.ensure(Permission.Ticket_SetTicketLineNewPrice), async (line: TicketLineDTO, price?: number): Promise<Result> => {

	if (line.Type === SoldProductType.MenuItem) {
		await Message.show(Res.Msg.MSG_CANNOT_ESTABLISH_PRICE_IN_MENU_ITEM_LINE());
		return Result.error();
	}

	const result = price !== undefined ? Result.ok(price) : await Modal.show(GetPrice, { line });
	if (result.ok) {
		await SalesStore.setLinePrice(line, result.value);
		await CustomerDisplay.displayExistingLine(SalesStore.getTicket(), SalesStore.getLineById(line.Id));
	}

	return result;
});